import React, {
  useRef,
  useState,
} from 'react';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';

import {
  useClickAway,
} from 'web-hooks';
import {
  formatTimeAgo,
  getEntityPath,
  placeCaretAtEnd,
} from 'web-utils';

import {
  Icon,
  Button,
  Tooltip,
  Checkbox,
  EntityIcon,
  MarkdownRenderer,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import * as S from '../styled';

type Props = {
  item: {
    id: number;
    name: string;
    is_root: boolean;
    selected: boolean;
    parent_id: number;
    updated_at: string;
    description: string;
  }
  basePath?: string;
  resourceType?: string;
  openedTooltips: string[];
  onSelect?: { (item: any): void };
  onChangeName?: { (item: any, newName: string | null): void };
  onRemoveEntityTooltip: { (id: number): void };
};

const Item = ({
  item,
  basePath,
  resourceType,
  onSelect,
  onChangeName,
  onRemoveEntityTooltip,
  openedTooltips,
}: Props) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const [editableName, setEditableName] = useState(false);
  const [descriptionVisible, setDescriptionState] = useState(false);

  const handleEdit = () => {
    const newName = nameRef.current?.textContent;
    if (newName && onChangeName) {
      onChangeName(item, newName);
    }
    setEditableName(false);
  };

  const ref = useClickAway(
    () => {
      setEditableName(false);
    },
    { disabled: !editableName },
  );

  const Name = editableName ? (
    <S.EditableWrapper
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleEdit();
        }
      }}
    >
      <Flex
        width="18px"
        height="20px"
        align="center"
        justify="center"
      >
        {item.parent_id ? (
          <Icon
            type="far fa-folder"
            size={14}
          />
        ) : (
          <EntityIcon
            resourceType={resourceType}
          />
        )}
      </Flex>
      <S.EditableName
        html={item.name}
        innerRef={nameRef}
      />
    </S.EditableWrapper>
  ) : (
    <S.Name
      to={getEntityPath(item, basePath)}
      onClick={() => onRemoveEntityTooltip(item.id)}
    >
      <Flex
        width="18px"
        height="20px"
        align="center"
        justify="center"
      >
        {item.parent_id ? (
          <Icon
            type="far fa-folder"
            size={14}
          />
        ) : (
          <EntityIcon
            resourceType={resourceType}
          />
        )}
      </Flex>
      {item.name}
    </S.Name>
  );

  return (
    <S.Item id={item.id}>
      <Flex justify="space-between" width="100%" align="center">
        <Flex
          spacing={10}
          align="center"
          justify="center"
        >
          <Checkbox
            checked={item.selected}
            disabled={item.is_root}
            onChange={() => onSelect(item)}
          />
          {openedTooltips.includes(String(item.id))
            ? (
              <Tooltip
                body={`Select a ${i18n.t(`short.label.${resourceType}`)}`}
                preferPlace="right"
                toggleMode
                initialState
              >
                {Name}
              </Tooltip>
            )
            : Name}
        </Flex>
        <Flex
          align="center"
          spacing={15}
        >
          {!item.is_root && (
            <Button
              ghost
              appearance="primary"
              className="is_hover_only"
              onClick={() => {
                setEditableName(!editableName);
                if (!editableName) {
                  setTimeout(() => {
                    placeCaretAtEnd(nameRef.current);
                  }, 0);
                }
              }}
            >
              Rename
            </Button>
          )}
          {(resourceType !== 'exercises' && item.description) && (
            <Button
              ghost
              icon={`fas fa-chevron-${descriptionVisible ? 'up' : 'down'}`}
              appearance="primary"
              className="is_hover_only"
              onClick={() => setDescriptionState(!descriptionVisible)}
            >
              See Description
            </Button>
          )}
          <S.Time
            title={DateTime.fromISO(item.updated_at).toLocaleString(DateTime.DATETIME_FULL)}
          >
            {formatTimeAgo(item.updated_at)}
          </S.Time>
        </Flex>
      </Flex>
      {descriptionVisible && (
        <Flex
          width="calc(100% - 150px)"
          padding="25px 50px"
        >
          <MarkdownRenderer
            source={item?.description}
          />
        </Flex>
      )}
    </S.Item>
  );
};

Item.defaultProps = {
  basePath: '/',
  resourceType: null,
  onSelect: () => {},
  onChangeName: null,
};

export default Item;
